import React from 'react';
import { Helmet } from 'react-helmet-async';
import { COMPANY_INFO } from '../utils/constants';

const PrivacyPolicy = () => {
  const sectionStyle = {
    marginBottom: '2rem',
  };

  const headingStyle = {
    fontFamily: '"Playfair Display", serif',
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1A1A1A',
    marginBottom: '1rem',
  };

  const paragraphStyle = {
    fontFamily: '"DM Sans", sans-serif',
    fontSize: '1rem',
    lineHeight: 1.8,
    color: '#444444',
    marginBottom: '0.75rem',
  };

  const listStyle = {
    fontFamily: '"DM Sans", sans-serif',
    fontSize: '1rem',
    lineHeight: 1.8,
    color: '#444444',
    paddingLeft: '1.5rem',
    marginBottom: '0.75rem',
  };

  return (
    <>
      <Helmet>
        <title>Privacy Policy | {COMPANY_INFO.name}</title>
        <meta name="description" content={`Privacy Policy for ${COMPANY_INFO.name}. Learn how we collect, use, and protect your personal information.`} />
      </Helmet>

      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '8rem 1.5rem 4rem',
      }}>
        <h1 style={{
          fontFamily: '"Playfair Display", serif',
          fontSize: 'clamp(2rem, 5vw, 3rem)',
          fontWeight: 700,
          color: '#1A1A1A',
          marginBottom: '0.5rem',
        }}>
          Privacy Policy
        </h1>
        <p style={{
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '0.9rem',
          color: '#888888',
          marginBottom: '3rem',
        }}>
          Last updated: October 2026
        </p>

        <div style={sectionStyle}>
          <p style={paragraphStyle}>
            {COMPANY_INFO.fullName} ("we", "our", or "us") operates the website {COMPANY_INFO.website} and mobile applications published under {COMPANY_INFO.name}. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our applications and services.
          </p>
          <p style={paragraphStyle}>
            By using our services, you agree to the collection and use of information in accordance with this policy.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>1. Information We Collect</h2>
          <p style={paragraphStyle}>We may collect the following types of information:</p>
          <p style={{ ...paragraphStyle, fontWeight: 600 }}>Personal Information</p>
          <ul style={listStyle}>
            <li>Name, email address, and phone number when you contact us or fill out a form</li>
            <li>Business name and industry details when you request a consultation</li>
            <li>Account credentials if you create an account on our platform</li>
          </ul>
          <p style={{ ...paragraphStyle, fontWeight: 600 }}>Usage Data</p>
          <ul style={listStyle}>
            <li>Device information (device type, operating system, unique device identifiers)</li>
            <li>Log data (IP address, browser type, pages visited, time spent)</li>
            <li>Analytics data through Google Analytics and Firebase Analytics</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>
          <p style={{ ...paragraphStyle, fontWeight: 600 }}>Information from Mobile Applications</p>
          <ul style={listStyle}>
            <li>Device identifiers and push notification tokens</li>
            <li>App usage data and interaction patterns</li>
            <li>Crash reports and performance data</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>2. How We Use Your Information</h2>
          <p style={paragraphStyle}>We use the information we collect to:</p>
          <ul style={listStyle}>
            <li>Provide, maintain, and improve our services, applications, and software products</li>
            <li>Respond to your inquiries and provide customer support</li>
            <li>Send you updates, marketing communications, and promotional materials (with your consent)</li>
            <li>Analyse usage patterns to improve user experience</li>
            <li>Detect, prevent, and address technical issues and security threats</li>
            <li>Comply with legal obligations and enforce our terms</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>3. Data Sharing and Disclosure</h2>
          <p style={paragraphStyle}>We do not sell your personal information. We may share your data with:</p>
          <ul style={listStyle}>
            <li><strong>Service Providers:</strong> Third-party vendors who assist in operating our services (e.g., hosting, analytics, payment processing)</li>
            <li><strong>Business Partners:</strong> With your consent, for joint offerings or integrations</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>4. Data Security</h2>
          <p style={paragraphStyle}>
            We implement industry-standard security measures to protect your personal information, including encryption, secure servers, and access controls. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>5. Data Retention</h2>
          <p style={paragraphStyle}>
            We retain your personal information only for as long as necessary to fulfil the purposes outlined in this policy, unless a longer retention period is required or permitted by law. When data is no longer needed, we securely delete or anonymise it.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>6. Your Rights</h2>
          <p style={paragraphStyle}>Depending on your jurisdiction, you may have the following rights:</p>
          <ul style={listStyle}>
            <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
            <li><strong>Deletion:</strong> Request deletion of your personal data</li>
            <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
            <li><strong>Data Portability:</strong> Request your data in a structured, machine-readable format</li>
          </ul>
          <p style={paragraphStyle}>
            To exercise any of these rights, please contact us at <a href={`mailto:${COMPANY_INFO.email}`} style={{ color: '#1A1A1A', fontWeight: 600 }}>{COMPANY_INFO.email}</a>.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>7. Cookies and Tracking Technologies</h2>
          <p style={paragraphStyle}>
            We use cookies and similar tracking technologies to track activity on our website and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, some features of our website may not function properly without cookies.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>8. Third-Party Services</h2>
          <p style={paragraphStyle}>
            Our services may contain links to third-party websites or integrate with third-party services (e.g., Google Analytics, Firebase, WhatsApp Business API). We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>9. Children's Privacy</h2>
          <p style={paragraphStyle}>
            Our services are not directed to individuals under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected such information, we will take steps to delete it promptly.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>10. Changes to This Policy</h2>
          <p style={paragraphStyle}>
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date. You are advised to review this page periodically for any changes.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>11. Contact Us</h2>
          <p style={paragraphStyle}>
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <ul style={listStyle}>
            <li><strong>Company:</strong> {COMPANY_INFO.fullName}</li>
            <li><strong>Email:</strong> <a href={`mailto:${COMPANY_INFO.email}`} style={{ color: '#1A1A1A', fontWeight: 600 }}>{COMPANY_INFO.email}</a></li>
            <li><strong>Website:</strong> {COMPANY_INFO.website}</li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
